import { Component, computed, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTableModule } from '@angular/material/table';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ConfigurationWaterfall } from '../configuration-waterfall.service';
import { WorkspaceStore } from '../workspace-store';
import { EnvironmentMeta } from '../environment-meta/environment-meta';
import {
  Environment, TableItem, TextFn,
  canCompare as canCompareFor, contentLabel as contentLabelFor, environmentLabel, environments, filterRows, groupRows,
  hasData as hasDataFor, identity, pairBadge as pairBadgeFor, payload, prettyJson,
} from '../configuration-view';

@Component({
  selector: 'app-overview',
  imports: [
    FormsModule, MatButtonModule, MatCardModule, MatFormFieldModule, MatIconModule, MatInputModule,
    MatSelectModule, MatTableModule, TranslocoPipe, EnvironmentMeta,
  ],
  templateUrl: './overview-page.html',
  styleUrl: './overview-page.scss',
})
export class OverviewPage {
  private readonly transloco = inject(TranslocoService);
  private readonly workspace = inject(WorkspaceStore);
  readonly rows = this.workspace.rows;
  readonly loading = this.workspace.loading;
  readonly loaded = this.workspace.loaded;
  readonly busyLabel = this.workspace.busyLabel;
  readonly search = this.workspace.search;
  readonly renderMode = this.workspace.renderMode;
  readonly environment = this.workspace.environment;
  readonly selected = this.workspace.selected;

  readonly environments = environments;
  readonly displayedColumns = ['configuration', 'stable', 'stableAlfa', 'alfa', 'alfaBeta', 'beta', 'betaProd', 'prod'];
  readonly filtered = computed(() => filterRows(this.rows(), this.search()));
  readonly grouped = computed(() => groupRows(this.filtered()));
  readonly isGroup = (_index: number, item: TableItem): boolean => item.kind === 'group';
  readonly isRow = (_index: number, item: TableItem): boolean => item.kind === 'row';
  readonly content = computed(() => {
    const row = this.selected();
    if (!row) return null;
    return {
      application: payload(row, this.environment(), 'Application'),
      apiCore: prettyJson(payload(row, this.environment(), 'ApiCore')),
    };
  });
  private readonly text: TextFn = key => this.transloco.translate(key);

  hasData(row: ConfigurationWaterfall, environment: Environment): boolean {
    return hasDataFor(row, environment);
  }

  contentLabel(row: ConfigurationWaterfall, environment: Environment): string {
    return contentLabelFor(row, environment, this.text);
  }

  canCompare(row: ConfigurationWaterfall, pair: number): boolean {
    return canCompareFor(row, pair);
  }

  pairBadge(row: ConfigurationWaterfall, pair: number): string {
    return pairBadgeFor(row, pair, this.renderMode(), this.transloco.getActiveLang(), this.text);
  }

  label(environment: Environment): string {
    return environmentLabel(environment);
  }

  isSelected(item: TableItem): boolean {
    if (item.kind !== 'row') return false;
    const selected = this.selected();
    return selected !== null && identity(selected) === identity(item.row);
  }

  openRow(item: TableItem): void {
    if (item.kind === 'row') this.workspace.showInCompare(item.row);
  }

  refresh(): void {
    this.workspace.refresh();
  }

  closePreview(): void {
    this.selected.set(null);
    this.workspace.dismissOverlays();
  }
}
